// Dashboard — central operator overview. Pulls the cross-service
// snapshot from /admin/dashboard, refetches every 10s, and pairs it
// with a live ticker tape so a quick glance shows the markets are
// healthy.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { fetchDashboard, fetchTickers, fetchRules, keys } from "@/lib/queries";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import {
  CurrencyModeScopeControl,
  DEFAULT_SCOPE,
  ScopeUnavailable,
  scopeKey,
  type CurrencyModeScope,
} from "@/components/dashboard/CurrencyModeScope";

// Hoisted so the literal never sits inside a JSX expression, where the
// i18n extractor would file "GAME" as a phrase to translate.
const SCOPE_GAME = "GAME";
import { currencyDashboardEnabled } from "@/lib/featureFlags";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { SparkAmount } from "@/components/ui/SparkAmount";
import { fmtRelative } from "@/lib/format";
import { cn } from "@/lib/cn";
import { useChannel } from "@/hooks/useChannel";
import { channels } from "@/lib/ws";
import { useState, useEffect, useMemo } from "react";
import type { TickerSnapshot } from "@/lib/types";
import { NeedsAttention } from "@/components/dashboard/NeedsAttention";
import { useTranslation } from "react-i18next";
import { intlLocale } from "@/i18n";

export const Route = createFileRoute("/_app/")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({ queryKey: keys.dashboard, queryFn: fetchDashboard }),
  component: DashboardPage,
});

function DashboardPage() {
  const { t, i18n } = useTranslation();
  const number = useMemo(
    () => new Intl.NumberFormat(intlLocale(i18n.resolvedLanguage === "th" ? "th" : "en")),
    [i18n.resolvedLanguage],
  );
  // SM-473: the scope is part of the key, not just of the render. A
  // reply from the previous scope files itself under the key it was
  // asked with, so it can never repaint the cards under a new label.
  const [scope, setScope] = useState<CurrencyModeScope>(DEFAULT_SCOPE);

  const dash = useQuery({
    queryKey: [...keys.dashboard, ...scopeKey(scope)],
    queryFn: fetchDashboard,
    refetchInterval: 10_000,
  });
  const tickers = useQuery({
    queryKey: keys.tickers(),
    queryFn: fetchTickers,
    refetchInterval: 30_000,
  });
  // The tickers feed carries only market_id, so resolve human-readable
  // names by joining against the catalog (same list the Markets page
  // loads, so this shares its React-Query cache). The map is keyed by
  // market_id; rows fall back to the raw id when a name isn't found
  // (e.g. a live instrument past the fetch limit).
  const markets = useQuery({
    queryKey: keys.rulesList(undefined, 200, 0),
    queryFn: () => fetchRules({ limit: 200, offset: 0 }),
    staleTime: 60_000,
  });
  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of markets.data?.items ?? []) m.set(r.market_id, r.name);
    return m;
  }, [markets.data]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">{t("dashboard.title")}</h1>
        <p className="text-sm text-muted">
          {t("dashboard.subtitle")}
        </p>
      </div>

      {currencyDashboardEnabled() && (
        <CurrencyModeScopeControl scope={scope} onChange={setScope} />
      )}

      {/* First section, per SM-310. The API omits what this admin may
          not see, so there is no permission logic here — an absent
          section is absent from the payload, not merely hidden. */}
      <NeedsAttention section={dash.data?.attention} loading={dash.isLoading} />

      {/* GAME is scoped but not yet counted: the dashboard summary is a
          single mode-less payload today (SM-482 is the read-path card
          that adds the dimension). Saying so beats printing CASH totals
          under a GAME heading, and beats zeroes — one is a lie about the
          numbers, the other a lie about the world. */}
      {currencyDashboardEnabled() && scope.mode === SCOPE_GAME && (
        <ScopeUnavailable reason="The dashboard summary is not yet reported per trading mode, so these figures cannot be scoped to GAME. They are shown for CASH only; switch back to CASH to read them." />
      )}

      <div
        className={cn(
          "grid grid-cols-2 gap-4 lg:grid-cols-4",
          // Under a GAME scope these are still CASH figures. The banner
          // above says so; dimming them says it again at the moment an
          // operator's eye lands on the number, which is where the
          // misreading would actually happen.
          currencyDashboardEnabled() && scope.mode === SCOPE_GAME && "opacity-60",
        )}
      >
        <StatCard label={t("dashboard.openMarkets")} value={formatCount(dash.data?.markets.open_rules, number)} sub={t("dashboard.total", { count: number.format(dash.data?.markets.total_rules ?? 0) })} />
        <StatCard label={t("dashboard.healthyPeers")} value={`${formatCount(dash.data?.engine.healthy_peers, number)} / ${formatCount(dash.data?.engine.peers, number)}`} sub={t("dashboard.instruments", { count: number.format(dash.data?.engine.owned_instruments ?? 0) })} />
        <StatCard label={t("dashboard.activeUsers")} value={formatCount(dash.data?.users.total_users, number)} sub={t("dashboard.allTime")} />
        <StatCard label={t("dashboard.openPositions")} value={formatCount(dash.data?.treasury.open_positions, number)} sub={t("dashboard.wallets", { count: number.format(dash.data?.treasury.wallets ?? 0) })} />
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <Card>
          <CardHeader className="border-b border-border">
            <CardTitle>{t("dashboard.liveTape")}</CardTitle>
            <span className="text-2xs text-muted">{t("dashboard.instruments", { count: number.format(tickers.data?.length ?? 0) })}</span>
          </CardHeader>
          <CardBody className="p-0">
            {tickers.isLoading && <Skeleton className="m-5 h-24" />}
            {tickers.data && tickers.data.length > 0 ? (
              <>
                <div className={cn(TAPE_GRID, "border-b border-border bg-surface-2 py-2")}>
                  <span className={cn(TAPE_HEAD, "col-span-5")}>{t("dashboard.tapeMarket")}</span>
                  <span className={cn(TAPE_HEAD, "col-span-3 text-right")}>{t("dashboard.tapeLast")}</span>
                  <span className={cn(TAPE_HEAD, "col-span-2 text-right")}>{t("dashboard.tapeBid")}</span>
                  <span className={cn(TAPE_HEAD, "col-span-2 text-right")}>{t("dashboard.tapeAsk")}</span>
                </div>
                <ul>
                  {tickers.data.slice(0, 12).map((row) => (
                    <LiveTickerRow key={row.market_id} initial={row} name={nameById.get(row.market_id)} />
                  ))}
                </ul>
              </>
            ) : (
              <p className="px-5 py-6 text-sm text-muted">{t("dashboard.noTickers")}</p>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader className="border-b border-border">
            <CardTitle>{t("dashboard.recentActivity")}</CardTitle>
            <Link to="/audit" className="text-2xs text-accent hover:underline">{t("dashboard.viewAll")}</Link>
          </CardHeader>
          <CardBody className="p-0">
            {dash.data?.recent_audit && dash.data.recent_audit.length > 0 ? (
              <ul className="divide-y divide-border/60">
                {dash.data.recent_audit.slice(0, 12).map((e) => (
                  <li key={e.event_id} className="flex items-start justify-between gap-3 px-5 py-2.5">
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge tone={
                          e.severity === "CRITICAL" || e.severity === "ERROR" ? "danger"
                          : e.severity === "WARN" ? "warning"
                          : "muted"
                        }>{e.severity}</Badge>
                        <span className="text-xs font-medium text-fg">{e.event_type}</span>
                      </div>
                      <div className="mt-0.5 text-2xs text-muted">
                        {e.target_kind && e.target_id ? `${e.target_kind}: ${e.target_id}` : ""}
                      </div>
                    </div>
                    <span className="text-2xs text-muted">{fmtRelative(e.occurred_at)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-5 py-6 text-sm text-muted">{t("dashboard.noAudit")}</p>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function formatCount(value: number | undefined, number: Intl.NumberFormat): string {
  return value === undefined ? "—" : number.format(value);
}

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <Card>
      <CardBody className="pt-5">
        <div className="text-2xs uppercase tracking-wide text-muted">{label}</div>
        <div className="num mt-1 text-3xl font-semibold tracking-tight">{value}</div>
        {sub && <div className="text-2xs text-muted">{sub}</div>}
      </CardBody>
    </Card>
  );
}

// Column geometry, shared by the header row and every ticker row so the
// titles cannot drift out of alignment with the values under them.
const TAPE_GRID = "grid grid-cols-12 items-center gap-3 px-5";
// Mirrors the <Th> styling the Activity tables use, so the two pages
// read as one console.
const TAPE_HEAD = "text-2xs font-medium uppercase tracking-wide text-muted";

// `name` is resolved from the catalog and passed in as a stable prop —
// kept separate from the WS-updated `tick` state so live price ticks
// (which carry only market_id) never clobber the displayed name.
function LiveTickerRow({ initial, name }: { initial: TickerSnapshot; name?: string }) {
  const [tick, setTick] = useState(initial);
  useEffect(() => setTick(initial), [initial]);
  useChannel<TickerSnapshot>(channels.ticker(initial.market_id), (data) => setTick(data));
  const named = name?.trim();
  return (
    <li className={cn(TAPE_GRID, "border-b border-border/60 py-2.5 last:border-0")}>
      <Link to="/markets" className="col-span-5 block min-w-0 hover:text-accent">
        {named ? (
          <>
            <span className="block truncate text-xs text-fg" title={named}>{named}</span>
            <span className="block truncate font-mono text-[10px] text-muted">{tick.market_id}</span>
          </>
        ) : (
          <span className="block truncate font-mono text-xs" title={tick.market_id}>{tick.market_id}</span>
        )}
      </Link>
      {/* SM-384: these are prices, and a price on the Activity pages is
          SPARK (⚡ = one cent of the wire's 4dp minor unit). Rendering
          them through fmtPrice instead showed the raw storage scale —
          the same 5000 tick read "0.5000" here and "⚡50" there. Same
          component, same truncation, same "—" placeholder as Activity,
          so an operator reconciling the two sees one number. */}
      <span className="num col-span-3 text-right text-sm font-semibold">
        <SparkAmount value={tick.last_price} trimZeros />
      </span>
      <span className="num col-span-2 text-right text-2xs text-muted">
        <SparkAmount value={tick.best_bid} trimZeros />
      </span>
      <span className="num col-span-2 text-right text-2xs text-muted">
        <SparkAmount value={tick.best_ask} trimZeros />
      </span>
    </li>
  );
}
